import { useMutation, useQueryClient } from "@tanstack/react-query";

import StylesAPI from "../../api/StylesAPI";

import type { FontFamily } from "../../api/StylesAPI.types";

export interface UseOrganizationFontMutationsReturn {
  isUploading: boolean;
  uploadFont: (fileList: File[]) => Promise<FontFamily>;
  deleteFont: (fontUuid: string) => Promise<void>;
}

export function useOrganizationFontMutations(): UseOrganizationFontMutationsReturn {
  const queryClient = useQueryClient();

  const availableFontQuery = StylesAPI.generateQuery({
    fn: StylesAPI.getAvailableFontList,
    params: [],
  });

  const invalidateFontList = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: availableFontQuery.queryKey,
    });
  };

  const uploadMutation = useMutation({
    mutationFn: async (fileList: File[]) =>
      StylesAPI.uploadFont({
        fileList,
        licenseDeclaration: { declared_at: new Date().toISOString() },
      }),
    onSuccess: invalidateFontList,
  });

  const deleteMutation = useMutation({
    mutationFn: async (fontUuid: string) => StylesAPI.deleteFont(fontUuid),
    onSuccess: invalidateFontList,
  });

  const uploadFont = async (fileList: File[]): Promise<FontFamily> => {
    const { family } = await uploadMutation.mutateAsync(fileList);

    return family;
  };

  const deleteFont = async (fontUuid: string): Promise<void> => {
    await deleteMutation.mutateAsync(fontUuid);
  };

  return {
    isUploading: uploadMutation.isLoading,
    uploadFont,
    deleteFont,
  };
}
